import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import {
  getSearchFromURLParams,
  useQueryParams,
} from "@mirage/search/hooks/useQueryParams";
import { useDualMode } from "@mirage/service-experimentation/useDualMode";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { typeahead } from "@mirage/service-typeahead-search/service/types";
import { usePreviousPath } from "@mirage/shared/hooks/usePreviousPath";
import { syncQueryFromURL } from "@mirage/shared/search/search-filters";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useLayoutEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { ModeAtom } from "../atoms/mode";

import type { TypeaheadSearch } from "@mirage/service-typeahead-search/hooks/useTypeaheadSearch";
import { isEsvConfig } from "@mirage/shared/util/esv";
import { AllFiltersByIdAtom } from "../atoms/filters";

const isSearchResultsPage = (path: string) => path === "/search_results";

export function useCaptureOmniboxURLParams({
  setTypeaheadQuery,
}: TypeaheadSearch) {
  const { isDualModeLauncher } = useDualMode();
  const setOmniboxScopedMode = useSetAtom(ModeAtom);
  const allFiltersById = useAtomValue(AllFiltersByIdAtom);
  const { searchAttemptSessionManager } = useMirageAnalyticsContext();
  const { previousPath } = usePreviousPath();
  const { pathname } = useLocation();
  const queryParams = useQueryParams();

  const isUsingTypeaheadFiltersFeatureFlag =
    useFeatureFlagValue("dash_2024_09_30_typeahead_filters") === "ON";

  const urlSearch = useMemo(
    () => getSearchFromURLParams(queryParams),
    [queryParams],
  );

  /**
   * When search is triggered from browser history or from home, we need to sync
   * the value in the typeahead with the query from browser history
   */
  const isTaggedQuery = useCallback(
    (activeQuery: string): boolean => {
      const searchAttemptSession = searchAttemptSessionManager.getSession();
      if (!searchAttemptSession) return false;
      const oldQuery = searchAttemptSession?.properties.query;
      const oldIsTypeAhead = searchAttemptSession?.properties.isTypeahead;
      return activeQuery === oldQuery && oldIsTypeAhead;
    },
    [searchAttemptSessionManager],
  );

  useLayoutEffect(() => {
    const hasLeftSearchPage: boolean =
      isSearchResultsPage(previousPath) && pathname !== previousPath;

    if (!hasLeftSearchPage) return;

    if (!isDualModeLauncher) {
      setTypeaheadQuery("");
    }
  }, [pathname, previousPath, setTypeaheadQuery, isDualModeLauncher]);

  useLayoutEffect(() => {
    if (isSearchResultsPage(pathname) && !isEsvConfig()) {
      setOmniboxScopedMode(typeahead.Mode.Find);
    } else {
      setOmniboxScopedMode(typeahead.Mode.Default);
    }
  }, [pathname, setOmniboxScopedMode]);

  useLayoutEffect(() => {
    const nextQuery = syncQueryFromURL({
      query: urlSearch.query,
      filters: isUsingTypeaheadFiltersFeatureFlag ? urlSearch.filters : [],
      allFiltersById,
    });

    if (!isDualModeLauncher) {
      setTypeaheadQuery(nextQuery, isTaggedQuery(urlSearch.query));
    }

    // We only want to run this effect when URL query/filters change
  }, [
    urlSearch.query,
    urlSearch.filters,
    isUsingTypeaheadFiltersFeatureFlag,
    isTaggedQuery,
    setTypeaheadQuery,
    isDualModeLauncher,
  ]);
}
